"use client";

import { useEffect, useRef, useState, type ComponentType } from "react";
import { Mail, MessageSquare, Send, TriangleAlert } from "lucide-react";
import {
  NothingToResendReason,
  ResendChannel,
  ResendChannelUnavailableReason,
  ResendNotificationType,
  type ResendChannelOption,
  type ResendOptions,
} from "lib/api-types/schemas";
import { Button } from "@clarityos/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@clarityos/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@clarityos/ui/dropdown-menu";
import {
  HoverTooltip,
  HoverTooltipContent,
  HoverTooltipTrigger,
} from "@clarityos/ui/hover-tooltip";
import { twMerge } from "tailwind-merge";
import { AnalyticsEvent, trackEvent } from "@/lib/analytics";
import { formatCurrency } from "@/lib/currency";
import { formatPhone } from "@/lib/string";
import { formatDate } from "@/lib/time";
import type {
  ResendFailureReason,
  ResendOutcome,
} from "../hooks/useResendNotification";

interface ResendNotificationMenuProps {
  options?: ResendOptions;
  isSending?: boolean;
  onSend: (
    channel: ResendChannel,
    confirmed: ResendOptions,
  ) => Promise<ResendOutcome>;
}

type IconComponent = ComponentType<{ className?: string }>;

const CHANNEL_NAMES: Record<ResendChannel, string> = {
  [ResendChannel.Sms]: "Text",
  [ResendChannel.Email]: "Email",
};

const CONTACT_NAMES: Record<ResendChannel, string> = {
  [ResendChannel.Sms]: "phone number",
  [ResendChannel.Email]: "email",
};

function getUnavailableReason(
  channel: ResendChannel,
  reason?: ResendChannelUnavailableReason,
) {
  switch (reason) {
    case ResendChannelUnavailableReason.OptedOut:
      return `${CHANNEL_NAMES[channel]} opted out by patient`;
    case ResendChannelUnavailableReason.Landline:
      return "Number is a landline";
    case ResendChannelUnavailableReason.NotTextable:
      return "Number is not verified for texts";
    case ResendChannelUnavailableReason.InvalidContact:
      return `Invalid ${CONTACT_NAMES[channel]}`;
    case ResendChannelUnavailableReason.UnderageGuarantor:
      return "Guarantor is under age";
    case ResendChannelUnavailableReason.NoActiveGuarantor:
      return "No active guarantor";
    default:
      return `No ${CONTACT_NAMES[channel]} on file`;
  }
}

const NOTHING_TO_RESEND_REASON: Record<NothingToResendReason, string> = {
  [NothingToResendReason.NoStatement]:
    "No ClarityPay statement has been sent to this patient",
  [NothingToResendReason.NothingSent]:
    "No notification has been sent for this statement yet",
  [NothingToResendReason.PaidWithoutReceipt]:
    "This statement was paid and has no receipt to resend",
};

function getNothingToResendReason(options?: ResendOptions) {
  if (!options) return null;
  if (options.nothingToResend) {
    return NOTHING_TO_RESEND_REASON[options.nothingToResend];
  }
  return options.statementId
    ? null
    : NOTHING_TO_RESEND_REASON[NothingToResendReason.NoStatement];
}

interface SendFailure {
  reason: ResendFailureReason;
  address: string;
}

function getFailureMessage(channel: ResendChannel, failure: SendFailure) {
  switch (failure.reason) {
    case "too_soon":
      return "A notification was just sent. Please wait a minute before resending.";
    case "recipient_changed":
      return `The patient's ${CONTACT_NAMES[channel]} changed to ${failure.address}. Check it and send again.`;
    case "statement_changed":
      return "The statement changed. Check the details and send again.";
    default:
      return `Couldn't send to ${failure.address}`;
  }
}

function formatAddress(channel: ResendChannel, address?: string | null) {
  if (channel === ResendChannel.Sms) return formatPhone(address ?? "") ?? "";
  return address ?? "";
}

export function ResendNotificationMenu({
  options,
  isSending = false,
  onSend,
}: ResendNotificationMenuProps) {
  const [pending, setPending] = useState<{
    channel: ResendChannel;
    options: ResendOptions;
  } | null>(null);
  const [failure, setFailure] = useState<SendFailure | null>(null);
  const [isSent, setIsSent] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const sendInFlight = useRef(false);

  const canResend = !!options?.statementId && !options.nothingToResend;
  const isReceipt =
    options?.notificationType === ResendNotificationType.PaymentReceipt;
  const pendingChannel = pending?.channel ?? null;
  const pendingOptions = pending?.options;
  const isPendingReceipt =
    pendingOptions?.notificationType === ResendNotificationType.PaymentReceipt;
  const isEmail = pendingChannel === ResendChannel.Email;
  const pendingOption = isEmail ? pendingOptions?.email : pendingOptions?.sms;
  const ChannelIcon = isEmail ? Mail : MessageSquare;

  const restoreTriggerFocus = (event: Event) => {
    event.preventDefault();
    triggerRef.current?.focus();
  };

  const closeConfirm = () => {
    setPending(null);
    setFailure(null);
  };

  useEffect(() => {
    if (
      failure?.reason !== "recipient_changed" &&
      failure?.reason !== "statement_changed"
    ) {
      return;
    }
    if (!options) return;
    setPending((current) =>
      current && current.options !== options
        ? { ...current, options }
        : current,
    );
  }, [failure, options]);

  const handleConfirm = async () => {
    if (!pending || sendInFlight.current) return;
    sendInFlight.current = true;
    try {
      const outcome = await onSend(pending.channel, pending.options);
      if (outcome.status === "failed") {
        setFailure({
          reason: outcome.reason,
          address: formatAddress(
            pending.channel,
            outcome.address ?? pendingOption?.address,
          ),
        });
        return;
      }
      closeConfirm();
      setIsSent(true);
    } finally {
      sendInFlight.current = false;
    }
  };

  const renderItem = (
    channel: ResendChannel,
    Icon: IconComponent,
    option?: ResendChannelOption,
  ) => {
    const isAvailable = !!option?.available;
    const item = (
      <DropdownMenuItem
        aria-disabled={!isAvailable}
        className={twMerge(
          "items-start gap-3",
          !isAvailable && "cursor-not-allowed opacity-50",
        )}
        onSelect={(event) => {
          if (!isAvailable) {
            event.preventDefault();
            return;
          }
          trackEvent(AnalyticsEvent.PatientBillingResend, {
            Channel: channel === ResendChannel.Sms ? "SMS" : "Email",
          });
          if (options) setPending({ channel, options });
        }}
      >
        <Icon className="mt-0.5 size-4 text-[#1884F9]" />
        <span className="flex flex-col">
          <span>
            {CHANNEL_NAMES[channel]}: Resend{" "}
            {isReceipt ? "receipt" : "ClarityPay link"}
          </span>
          <span className="text-xs font-normal text-[#6B7280]">
            {formatAddress(channel, option?.address)}
          </span>
        </span>
      </DropdownMenuItem>
    );
    if (isAvailable) return item;

    return (
      <HoverTooltip>
        <HoverTooltipTrigger asChild>{item}</HoverTooltipTrigger>
        <HoverTooltipContent side="top">
          {getUnavailableReason(channel, option?.unavailableReason)}
        </HoverTooltipContent>
      </HoverTooltip>
    );
  };

  const disabledReason = isSending ? null : getNothingToResendReason(options);
  const trigger = (
    <DropdownMenuTrigger asChild>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="md"
        startIcon={Send}
        loading={isSending}
        aria-label="Send statement notification"
        disabled={!canResend}
      />
    </DropdownMenuTrigger>
  );

  return (
    <>
      <DropdownMenu>
        {disabledReason ? (
          <HoverTooltip>
            <HoverTooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex">
                {trigger}
              </span>
            </HoverTooltipTrigger>
            <HoverTooltipContent side="bottom">
              {disabledReason}
            </HoverTooltipContent>
          </HoverTooltip>
        ) : (
          trigger
        )}
        <DropdownMenuContent align="end" className="min-w-64">
          <DropdownMenuLabel>Send to patient</DropdownMenuLabel>
          {renderItem(ResendChannel.Sms, MessageSquare, options?.sms)}
          {renderItem(ResendChannel.Email, Mail, options?.email)}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={!!pendingChannel}
        onOpenChange={(open) => !open && !isSending && closeConfirm()}
      >
        <DialogContent
          size="sm"
          showCloseButton={false}
          className="flex flex-col items-center gap-4 text-center"
          onCloseAutoFocus={restoreTriggerFocus}
        >
          <div className="flex size-10 items-center justify-center rounded-lg bg-[#EFF6FF] text-[#1884F9]">
            <ChannelIcon className="size-5" />
          </div>
          <DialogHeader className="items-center text-center">
            <DialogTitle>
              Resend {isPendingReceipt ? "receipt" : "payment link"} by{" "}
              {isEmail ? "email" : "text"}?
            </DialogTitle>
            <DialogDescription>
              {isEmail ? "Email" : "Text"} new{" "}
              {isPendingReceipt ? "receipt" : "payment notification"} to{" "}
              {pendingChannel &&
                formatAddress(pendingChannel, pendingOption?.address)}
            </DialogDescription>
          </DialogHeader>
          <div className="grid w-full grid-cols-2 gap-2 text-left text-sm">
            <div className="rounded-lg bg-[#F9FAFB] p-3">
              <p className="text-xs text-[#6B7280]">Statement</p>
              <p>
                {pendingOptions?.statementDate
                  ? formatDate(pendingOptions.statementDate, "MM-dd-yyyy")
                  : ""}
              </p>
            </div>
            <div className="rounded-lg bg-[#F9FAFB] p-3">
              <p className="text-xs text-[#6B7280]">Amount</p>
              <p>
                {typeof pendingOptions?.amount === "number"
                  ? formatCurrency(pendingOptions.amount)
                  : ""}
              </p>
            </div>
          </div>
          {failure && pendingChannel && (
            <div
              role="alert"
              className="flex w-full items-center gap-2 rounded-md bg-[#CE2C31] px-3 py-2 text-left text-sm text-white"
            >
              <TriangleAlert className="size-4 shrink-0" />
              {getFailureMessage(pendingChannel, failure)}
            </div>
          )}
          <DialogFooter className="w-full pt-0">
            <DialogClose asChild>
              <Button
                variant="secondary"
                className="flex-1 rounded-full"
                disabled={isSending}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              className="flex-1 rounded-full"
              onClick={handleConfirm}
              loading={isSending}
            >
              Yes, send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isSent} onOpenChange={setIsSent}>
        <DialogContent
          size="sm"
          showCloseButton={false}
          className="flex flex-col items-center gap-4 text-center"
          onCloseAutoFocus={restoreTriggerFocus}
        >
          <div className="flex size-12 items-center justify-center rounded-full bg-[#1884F9] text-white">
            <Send className="size-5" />
          </div>
          <DialogHeader className="items-center text-center">
            <DialogTitle>Notification is being sent</DialogTitle>
            <DialogDescription>
              It may take up to a few minutes to deliver. If the patient
              doesn&apos;t receive it right away, proceed with collecting
              payment manually.
            </DialogDescription>
          </DialogHeader>
          <DialogClose asChild>
            <Button className="w-full rounded-full">Done</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </>
  );
}
